import type { FC } from 'react';
import { cn } from '@/utils/cn';
import { CheckIcon } from '../icons/CheckIcon';
import { DashIcon } from '../icons/DashIcon';
import { LogoPiece5Icon } from '../icons/LogoPiece5Icon';
import { LogoPiece6Icon } from '../icons/LogoPiece6Icon';

interface ProgressBarProps {
  progress: number;
  type?: 'small' | 'medium' | 'large';
}

const AdvancedProgressBar: FC<ProgressBarProps> = ({
  progress,
  type = 'small',
}) => {
  const getVariant = () => {
    if (progress >= 90) {
      return {
        gradient: 'bg-light-green-gradient',
        color: 'bg-green',
        interval: '90 - 100%',
        icon: (
          <CheckIcon
            className="m-3.5 text-green"
            desc="Check mark icon for excellent rating"
            title="Excellent"
          />
        ),
        text: 'One of the best bookmakers around. Enjoyed it a lot. ',
      };
    }
    if (progress >= 75 && progress < 90) {
      return {
        gradient: 'bg-dark-green-gradient',
        color: 'bg-green',
        interval: '75 - 89%',
        icon: (
          <CheckIcon
            className="m-3.5 text-green"
            desc="Check mark icon for great rating"
            title="Great"
          />
        ),
        text: 'Awesome offers and great selection of games and events. ',
      };
    }
    if (progress >= 65 && progress < 75) {
      return {
        gradient: 'bg-dark-blue-gradient',
        color: 'bg-light-blue',
        interval: '65 - 74%',
        icon: (
          <CheckIcon
            className="m-3.5 text-light-blue"
            desc="Check mark icon for good rating"
            title="Good"
          />
        ),
        text: 'The core stuff is great but it lacks some features.',
      };
    }
    if (progress >= 50 && progress < 65) {
      return {
        gradient: 'bg-dark-orange-gradient',
        color: 'bg-orange',
        interval: '50 - 64%',
        icon: (
          <CheckIcon
            className="m-3.5 text-light-blue"
            desc="Check mark icon for average rating"
            title="Average"
          />
        ),
        text: 'Good events and offers but has sloppy ux one some games.',
      };
    }
    return {
      gradient: 'bg-dark-red-gradient',
      color: 'bg-red',
      interval: '1 - 49%',
      icon: (
        <DashIcon
          className="m-3.5 text-gray"
          desc="Dash icon for poor rating"
          title="Poor"
        />
      ),
      text: 'Some users had problems. Proceed with caution.',
    };
  };

  const variant = getVariant();

  if (type === 'small') {
    return (
      <div className="h-2 w-full rounded bg-white-o15">
        <div
          className={cn('h-2 rounded', variant.color)}
          style={{ width: `${progress}%` }}
        />
      </div>
    );
  }
  if (type === 'medium') {
    return (
      <div className="relative z-9 flex h-[110px] w-[249px] flex-col justify-center gap-2 rounded-lg bg-light-green-gradient px-3">
        <div
          className={
            progress > 89
              ? 'absolute bottom-0 left-0 bg-contain bg-no-repeat'
              : 'hidden'
          }
          style={{
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
          }}
        >
          <LogoPiece5Icon
            desc="GoCore logo piece decoration"
            title="Logo Decoration"
          />
        </div>
        <div className="z-10 flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between">
            <p className="font-normal text-gray text-xs leading-3 tracking-[0.01em]">
              Our Ranking
            </p>
            <p className="font-semibold text-sm text-white leading-[25px]">
              {progress}%
            </p>
          </div>
          <div className="h-2 w-full rounded bg-white-o15">
            <div
              className={cn('h-2 rounded', variant.color)}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className={cn(
        'relative z-9 flex w-full items-center justify-center rounded-lg py-8',
        variant.gradient
      )}
    >
      <div
        className={
          progress >= 90
            ? 'absolute bottom-0 left-0 bg-contain bg-no-repeat'
            : 'hidden'
        }
        style={{
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
        }}
      >
        <LogoPiece6Icon
          desc="GoCore logo piece decoration"
          title="Logo Decoration"
        />
      </div>

      <div className="flex w-[307px] flex-col gap-1">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-row justify-between">
            <p className="font-light text-[24px] leading-[24px]">Our Ranking</p>
            <p className="font-semibold text-[24px] leading-[25px]">
              {progress}%
            </p>
          </div>

          <div className="h-2 w-full rounded bg-[#0E0E0F]">
            <div
              className={cn('h-2 rounded', variant.color)}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex flex-row items-center">
          {variant.icon}
          <p className="w-[220px] font-normal text-gray text-sm leading-[18px]">
            {variant.text}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdvancedProgressBar;
